if (!customElements.get('ps-map-gallery')) {
  customElements.define('ps-map-gallery', class extends HTMLElement {
    connectedCallback() {
      this.abort?.abort();
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.track = this.querySelector('.ps-map-gallery__track');
      this.slides = [...this.querySelectorAll('[data-ps-slide]')];
      this.thumbs = [...this.querySelectorAll('[data-ps-thumb]')];
      this.index = 0;
      const move = (index) => {
        const slide = this.slides[Math.max(0, Math.min(index, this.slides.length - 1))];
        this.track.scrollTo({ left: slide.offsetLeft - this.slides[0].offsetLeft, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
      };
      this.addEventListener('click', (event) => {
        const target = event.target.closest('button');
        if (!target) return;
        if (target.hasAttribute('data-ps-prev')) move(this.index - 1);
        if (target.hasAttribute('data-ps-next')) move(this.index + 1);
        if (target.hasAttribute('data-ps-thumb')) move(Number(target.dataset.psThumb));

      }, options);
      this.track.addEventListener('keydown', (event) => {
        if (event.target !== this.track) return;
        if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
          event.preventDefault();
          move(this.index + (event.key === 'ArrowRight' ? 1 : -1));
        }
      }, options);
      const update = () => {
        this.index = Math.max(0, Math.min(this.slides.length - 1, Math.round(this.track.scrollLeft / this.track.clientWidth)));
        this.querySelector('[data-ps-count]').textContent = `${this.index + 1} / ${this.slides.length}`;
        this.querySelectorAll('[data-ps-prev]').forEach((button) => { button.disabled = this.index === 0; });
        this.querySelectorAll('[data-ps-next]').forEach((button) => { button.disabled = this.index === this.slides.length - 1; });
        this.thumbs.forEach((thumb, i) => thumb.setAttribute('aria-current', String(i === this.index)));
        // Die Galerie ist so hoch wie das gezeigte Bild: Ohne Bildunterschrift kein leerer Balken darunter.
        const height = this.slides[this.index]?.offsetHeight;
        if (height) this.track.style.height = `${height}px`;
      };
      this.track.addEventListener('scroll', update, { ...options, passive: true });
      this.resize = new ResizeObserver(update);
      this.resize.observe(this.track);
      this.slides.forEach((slide) => this.resize.observe(slide));
      update();
    }
    disconnectedCallback() { this.abort?.abort(); this.resize?.disconnect(); }
  });
}

if (!customElements.get('ps-map-page')) {
  customElements.define('ps-map-page', class extends HTMLElement {
    connectedCallback() {
      this.abort?.abort();
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.sticky = this.querySelector('[data-ps-sticky]');
      this.status = this.querySelector('[data-ps-status]');
      this.sizes = this.querySelector('.ps-map-sizes');
      this.addEventListener('click', (event) => {
        const opener = event.target.closest('[data-ps-design]');
        if (opener) this.openDesigner(opener);
        if (event.target.closest('[data-ps-size-open]')) this.sizes.showModal();
        const size = event.target.closest('[data-ps-size]');
        if (size) this.showSize(size);
      }, options);
      // Formatwahl: Preis folgt der Wahl, die Größenberatung zeigt dasselbe Format.
      this.addEventListener('change', (event) => {
        if (event.target.matches('.ps-map-format input')) this.chooseFormat(event.target);
      }, options);
      this.addEventListener('click', (event) => {
        const more = event.target.closest('[data-ps-formats-more]');
        if (!more) return;
        const open = more.getAttribute('aria-expanded') !== 'true';
        more.setAttribute('aria-expanded', String(open));
        more.querySelector('span').textContent = open ? '−' : '+';
        document.getElementById(more.getAttribute('aria-controls')).hidden = !open;
      }, options);
      const checkedFormat = this.querySelector('.ps-map-format input:checked');
      const firstSize = (checkedFormat && this.sizeButton(checkedFormat)) || this.querySelector('[data-ps-size]');
      if (firstSize) this.showSize(firstSize);
      this.syncVariante();
      this.querySelectorAll('dialog').forEach((dialog) => {
        dialog.addEventListener('click', (event) => {
          if (event.target !== dialog) return;
          const rect = dialog.getBoundingClientRect();
          if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) dialog.close();
        }, options);
        dialog.addEventListener('close', () => this.updateSticky(), options);
      });
      this.visibleActions = new Set();
      this.intersections = new IntersectionObserver((entries) => {
        for (const entry of entries) entry.isIntersecting ? this.visibleActions.add(entry.target) : this.visibleActions.delete(entry.target);
        this.updateSticky();
      }, { threshold: 0.15 });
      const actions = [...this.querySelectorAll('[data-ps-main-cta], .ps-map-quality [data-ps-design], .ps-map-finale [data-ps-design]'), document.querySelector('[data-ps-weiter]')].filter(Boolean);
      actions.forEach((action) => this.intersections.observe(action));
      this.mutations = new MutationObserver(() => {
        cancelAnimationFrame(this.frame);
        this.frame = requestAnimationFrame(() => this.updateSticky());
      });
      this.mutations.observe(document.body, { childList: true });
      this.querySelectorAll('dialog').forEach((dialog) => this.mutations.observe(dialog, { attributes: true, attributeFilter: ['open'] }));
      window.addEventListener('scroll', () => this.updateSticky(), { ...options, passive: true });
      this.updateSticky();
    }
    sizeButton(input) {
      return [...this.querySelectorAll('[data-ps-size]')].find((b) => b.dataset.psSize === input.dataset.psSizeTitle);
    }
    chooseFormat(input) {
      this.querySelectorAll('[data-ps-from]').forEach((from) => { from.hidden = true; });
      this.querySelectorAll('[data-ps-price]').forEach((price) => { price.textContent = input.dataset.psPrice; });
      const size = this.sizeButton(input);
      if (size) this.showSize(size);
      this.syncVariante();
    }
    // Gewähltes Format steht am Designer-Block als Startformat (data-variante = Shopify-Varianten-ID).
    // Gilt für alle Gestalten-Knöpfe; wirkt, sobald der Customizer data-variante liest.
    syncVariante() {
      const block = this.querySelector('[data-twk-poster]');
      const format = this.querySelector('.ps-map-format input:checked');
      if (block && format) block.dataset.variante = format.value;
    }
    showSize(button) {
      const title = button.dataset.psSize;
      const din = { A5: [14.8, 21], A4: [21, 29.7], A3: [29.7, 42], A2: [42, 59.4], A1: [59.4, 84.1], A0: [84.1, 118.9] };
      const dinKey = title.match(/\bA[0-5]\b/i)?.[0]?.toUpperCase();
      const pair = title.replaceAll(',', '.').match(/(\d+(?:\.\d+)?)\s*[x×]\s*(\d+(?:\.\d+)?)/i);
      const dimensions = din[dinKey] || (pair ? [Number(pair[1]), Number(pair[2])] : null);
      this.querySelectorAll('[data-ps-size]').forEach((item) => item.setAttribute('aria-pressed', String(item === button)));
      const svg = this.sizes.querySelector('svg');
      svg.toggleAttribute('hidden', !dimensions);
      if (dimensions) {
        const [width, height] = dimensions;
        const poster = this.querySelector('[data-ps-size-poster]');
        for (const [key, value] of Object.entries({ x: (240 - width) / 2, y: 95 - height, width, height })) poster.setAttribute(key, value);
      }
      this.querySelector('[data-ps-size-caption]').textContent = `${title} · ${button.querySelector('span').textContent}`;
    }
    updateSticky() {
      const hero = document.querySelector('.ps-hero--produkt');
      const passedHero = !hero || hero.getBoundingClientRect().bottom <= 0;
      const overlayOpen = !!document.querySelector('.twk-bk-overlay, dialog[open]');
      if (!this.sticky) return;
      this.sticky.hidden = !passedHero || this.visibleActions.size > 0 || overlayOpen;
    }
    openDesigner(button) {
      clearTimeout(this.loadingTimer);
      const block = this.querySelector('[data-twk-poster]');
      const trigger = block?.querySelector('[data-twk-pd-oeffnen]');
      if (!trigger) return;
      const start = Date.now();
      const open = () => {
        if (!this.isConnected) return;
        if (block.dataset.bereit !== '1') {
          this.status.textContent = this.status.dataset.loading;
          if (Date.now() - start < 10000) { this.loadingTimer = setTimeout(open, 200); return; }
          this.status.textContent = this.status.dataset.error;
          return;
        }
        this.status.textContent = '';
        const previous = ['vorauswahl', 'kartenstil', 'designs'].map((key) => [key, block.getAttribute(`data-${key}`)]);
        const style = button.dataset.psStyle;
        if (style && ['hell', 'monochrom', 'monochrom-schwarz'].includes(style)) {
          block.dataset.vorauswahl = `kartenstil=${style}`;
          block.dataset.kartenstil = style;
          block.dataset.designs = 'aus';
        }
        trigger.click();
        for (const [key, value] of previous) value === null ? block.removeAttribute(`data-${key}`) : block.setAttribute(`data-${key}`, value);
        this.updateSticky();
      };
      open();
    }
    disconnectedCallback() {
      this.abort?.abort(); this.intersections?.disconnect(); this.mutations?.disconnect();
      clearTimeout(this.loadingTimer); cancelAnimationFrame(this.frame);
    }
  });
}
